import React, { useState, useRef, useCallback } from "react";
import axios from "axios";

// const API = "http://localhost:5000";
const API = "https://abridge-spectacle-nintendo.ngrok-free.dev";

// ─── Icons (inline SVG, no extra deps) ────────────────────────────────────────
const UploadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}
    className="w-10 h-10">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
  </svg>
);

const PrintIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}
    className="w-6 h-6">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
    className="w-12 h-12">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
  </svg>
);

const FileIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}
    className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
  </svg>
);

// ─── Razorpay loader ───────────────────────────────────────────────────────────
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload  = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

// ─── Step indicator ────────────────────────────────────────────────────────────
function Steps({ current }) {
  const steps = ["Upload", "Options", "Pay"];
  return (
    <div className="flex items-center justify-center gap-0 mb-8">
      {steps.map((label, i) => {
        const done    = i < current;
        const active  = i === current;
        return (
          <React.Fragment key={label}>
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all
                ${done   ? "bg-green-500 text-white"  : ""}
                ${active ? "bg-indigo-600 text-white ring-4 ring-indigo-200" : ""}
                ${!done && !active ? "bg-gray-200 text-gray-400" : ""}
              `}>
                {done ? "✓" : i + 1}
              </div>
              <span className={`text-xs mt-1 font-medium ${active ? "text-indigo-600" : "text-gray-400"}`}>
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className={`h-0.5 w-16 mx-1 mb-4 transition-all ${done ? "bg-green-400" : "bg-gray-200"}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Upload Screen ─────────────────────────────────────────────────────────────
function UploadScreen({ onUploaded }) {
  const [dragging, setDragging]     = useState(false);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");
  const [selectedFile, setSelected] = useState(null);
  const inputRef                    = useRef();

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    const allowed = ["application/pdf", "image/png", "image/jpeg"];
    if (!allowed.includes(file.type)) {
      setError("Only PDF, PNG, and JPG files are allowed.");
      return;
    }
    setSelected(file);
    setError("");
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setLoading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", selectedFile);
      const { data } = await axios.post(`${API}/upload`, fd);
      onUploaded(data);
    } catch (e) {
      setError(e.response?.data?.error || "Upload failed. Is the backend running?");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current.click()}
        className={`border-2 border-dashed rounded-2xl p-10 flex flex-col items-center gap-3 cursor-pointer transition-all
          ${dragging ? "border-indigo-500 bg-indigo-50" : "border-gray-300 hover:border-indigo-400 hover:bg-gray-50"}
          ${selectedFile ? "border-green-400 bg-green-50" : ""}
        `}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg"
          className="hidden"
          onChange={(e) => handleFile(e.target.files[0])}
        />
        {selectedFile ? (
          <>
            <div className="text-green-500"><FileIcon /></div>
            <p className="font-semibold text-gray-700 text-center break-all">{selectedFile.name}</p>
            <p className="text-sm text-gray-400">
              {(selectedFile.size / 1024).toFixed(1)} KB · Click to change
            </p>
          </>
        ) : (
          <>
            <div className="text-indigo-400"><UploadIcon /></div>
            <p className="font-semibold text-gray-600">Drop your file here</p>
            <p className="text-sm text-gray-400">PDF, PNG, JPG supported</p>
          </>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 text-sm">
          ⚠ {error}
        </div>
      )}

      <button
        onClick={handleUpload}
        disabled={!selectedFile || loading}
        className="w-full py-3.5 rounded-xl font-semibold text-white bg-indigo-600
          hover:bg-indigo-700 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {loading ? "Uploading…" : "Upload & Continue →"}
      </button>
    </div>
  );
}

// ─── Options Screen ────────────────────────────────────────────────────────────
function OptionsScreen({ job, onOrderCreated }) {
  const [copies, setCopies] = useState(1);
  const [color, setColor]   = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState("");

  const rate  = color ? 10 : 3;
  const total = job.pages * copies * rate;

  const handleProceed = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/create-order/${job.job_id}`, {
        copies,
        color,
      });
      onOrderCreated(data);
    } catch (e) {
      setError(e.response?.data?.error || "Failed to create order.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {/* File info */}
      <div className="bg-indigo-50 rounded-xl px-4 py-3 flex items-center gap-3">
        <div className="text-indigo-500"><FileIcon /></div>
        <div>
          <p className="font-semibold text-gray-700 text-sm truncate max-w-[200px]">{job.filename}</p>
          <p className="text-xs text-gray-400">{job.pages} page{job.pages !== 1 ? "s" : ""} detected</p>
        </div>
      </div>

      {/* Copies */}
      <div>
        <label className="block text-sm font-semibold text-gray-600 mb-2">Number of Copies</label>
        <div className="flex items-center gap-4">
          <button
            onClick={() => setCopies(Math.max(1, copies - 1))}
            className="w-10 h-10 rounded-full border-2 border-gray-300 text-lg font-bold
              hover:border-indigo-500 hover:text-indigo-600 transition-all active:scale-90"
          >−</button>
          <span className="text-2xl font-bold text-gray-800 w-8 text-center">{copies}</span>
          <button
            onClick={() => setCopies(Math.min(20, copies + 1))}
            className="w-10 h-10 rounded-full border-2 border-gray-300 text-lg font-bold
              hover:border-indigo-500 hover:text-indigo-600 transition-all active:scale-90"
          >+</button>
        </div>
      </div>

      {/* Color mode */}
      <div>
        <label className="block text-sm font-semibold text-gray-600 mb-2">Print Mode</label>
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: "Black & White", value: false, price: "₹3/page", emoji: "⬛" },
            { label: "Color",         value: true,  price: "₹10/page", emoji: "🎨" },
          ].map((opt) => (
            <button
              key={String(opt.value)}
              onClick={() => setColor(opt.value)}
              className={`flex flex-col items-center gap-1 p-4 rounded-xl border-2 transition-all
                ${color === opt.value
                  ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                  : "border-gray-200 hover:border-gray-300 text-gray-600"
                }`}
            >
              <span className="text-2xl">{opt.emoji}</span>
              <span className="font-semibold text-sm">{opt.label}</span>
              <span className="text-xs text-gray-400">{opt.price}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Cost summary */}
      <div className="bg-gray-50 rounded-xl px-4 py-4 space-y-2">
        <div className="flex justify-between text-sm text-gray-500">
          <span>Pages × Copies × Rate</span>
          <span>{job.pages} × {copies} × ₹{rate}</span>
        </div>
        <div className="flex justify-between font-bold text-gray-800 text-lg border-t pt-2 mt-2">
          <span>Total</span>
          <span className="text-indigo-600">₹{total}</span>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 text-sm">
          ⚠ {error}
        </div>
      )}

      <button
        onClick={handleProceed}
        disabled={loading}
        className="w-full py-3.5 rounded-xl font-semibold text-white bg-indigo-600
          hover:bg-indigo-700 active:scale-95 transition-all disabled:opacity-40 flex items-center justify-center gap-2"
      >
        <PrintIcon />
        {loading ? "Creating order…" : `Pay ₹${total} Now`}
      </button>
    </div>
  );
}

// ─── Payment Screen ────────────────────────────────────────────────────────────
function PaymentScreen({ job, order, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState("");

  const handlePay = async () => {
    setLoading(true);
    setError("");

    const loaded = await loadRazorpayScript();
    if (!loaded) {
      setError("Failed to load Razorpay. Check your internet connection.");
      setLoading(false);
      return;
    }

    const options = {
      key:         order.key,
      amount:      order.amount,
      currency:    order.currency,
      name:        "QR Print Service",
      description: `Printing: ${job.filename}`,
      order_id:    order.order_id,
      theme:       { color: "#4f46e5" },
      prefill:     { name: "", email: "", contact: "" },
      handler: async (response) => {
        try {
          const { data } = await axios.post(`${API}/verify-payment`, {
            job_id:               job.job_id,
            razorpay_order_id:    response.razorpay_order_id,
            razorpay_payment_id:  response.razorpay_payment_id,
            razorpay_signature:   response.razorpay_signature,
          });
          console.log(data);
          onSuccess(data);
        } catch (e) {
          setError(e.response?.data?.error || "Payment verification failed.");
          setLoading(false);
        }
      },
      modal: {
        ondismiss: () => setLoading(false),
      },
    };

    const rp = new window.Razorpay(options);
    rp.on("payment.failed", (r) => {
      setError(`Payment failed: ${r.error.description}`);
      setLoading(false);
    });
    rp.open();
  };

  const totalInr = (order.amount / 100).toFixed(0);

  return (
    <div className="flex flex-col gap-5">
      {/* Order summary */}
      <div className="bg-indigo-50 rounded-xl px-5 py-5 space-y-3">
        <h3 className="font-bold text-gray-700 text-sm uppercase tracking-wide">Order Summary</h3>
        <div className="flex justify-between text-sm text-gray-600">
          <span>File</span>
          <span className="font-medium truncate max-w-[180px]">{job.filename}</span>
        </div>
        <div className="flex justify-between text-sm text-gray-600">
          <span>Pages</span>
          <span className="font-medium">{order.pages}</span>
        </div>
        <div className="flex justify-between text-sm text-gray-600">
          <span>Mode</span>
          <span className="font-medium">{order.rate === 10 ? "🎨 Color" : "⬛ B/W"}</span>
        </div>
        <div className="flex justify-between text-sm text-gray-600">
          <span>Rate</span>
          <span className="font-medium">₹{order.rate}/page</span>
        </div>
        <div className="border-t pt-3 flex justify-between font-bold text-gray-800 text-lg">
          <span>Total</span>
          <span className="text-indigo-600">₹{totalInr}</span>
        </div>
      </div>

      {/* Test card hint */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3 text-xs text-yellow-800 space-y-1">
        <p className="font-bold">🧪 Razorpay Test Mode</p>
        <p>Card: <span className="font-mono">4111 1111 1111 1111</span></p>
        <p>Expiry: Any future date &nbsp;|&nbsp; CVV: Any 3 digits</p>
        <p>OTP: <span className="font-mono">1234</span></p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 text-sm">
          ⚠ {error}
        </div>
      )}

      <button
        onClick={handlePay}
        disabled={loading}
        className="w-full py-4 rounded-xl font-bold text-white bg-gradient-to-r from-indigo-600 to-violet-600
          hover:from-indigo-700 hover:to-violet-700 active:scale-95 transition-all disabled:opacity-40 text-lg shadow-lg shadow-indigo-200"
      >
        {loading ? "Opening payment…" : `🔒 Pay ₹${totalInr} Securely`}
      </button>
    </div>
  );
}

// ─── Success Screen ────────────────────────────────────────────────────────────
function SuccessScreen({ result, onReset }) {
  return (
    <div className="flex flex-col items-center gap-6 text-center">
      <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center text-green-500 animate-bounce">
        <CheckIcon />
      </div>
      <div>
        <h2 className="text-2xl font-extrabold text-gray-800">Payment Successful!</h2>
        <p className="text-gray-500 mt-1">Your file has been sent to the printer.</p>
      </div>

      {/* Token */}
      <div className="w-full bg-gradient-to-br from-indigo-600 to-violet-600 rounded-2xl px-6 py-8 text-white shadow-xl shadow-indigo-200">
        <p className="text-sm font-semibold opacity-80 uppercase tracking-widest mb-2">Your Print Token</p>
        <p className="text-6xl font-extrabold tracking-wider">{result.token}</p>
        <p className="text-sm opacity-70 mt-4">Show this token at the counter to collect your print</p>
      </div>

      <div className="bg-gray-50 rounded-xl w-full px-4 py-3 text-sm text-gray-500">
        File: <span className="font-medium text-gray-700">{result.filename}</span>
      </div>

      <button
        onClick={onReset}
        className="w-full py-3 rounded-xl border-2 border-gray-200 text-gray-600 font-semibold
          hover:border-indigo-400 hover:text-indigo-600 transition-all"
      >
        ← Print Another File
      </button>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function App() {
  const [step,   setStep]   = useState(0);   // 0=upload 1=options 2=payment 3=success
  const [job,    setJob]    = useState(null);
  const [order,  setOrder]  = useState(null);
  const [result, setResult] = useState(null);

  const reset = () => {
    setStep(0);
    setJob(null);
    setOrder(null);
    setResult(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-indigo-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">

        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-sm mb-4">
            <span className="text-xl">🖨</span>
            <span className="font-bold text-gray-700 tracking-tight">QR Print Station</span>
          </div>
          <p className="text-gray-400 text-sm">Upload · Configure · Pay · Print</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-3xl shadow-xl shadow-indigo-100 px-6 py-8">

          {step < 3 && <Steps current={step} />}

          {step === 0 && (
            <UploadScreen
              onUploaded={(data) => { setJob(data); setStep(1); }}
            />
          )}

          {step === 1 && job && (
            <OptionsScreen
              job={job}
              onOrderCreated={(data) => { setOrder(data); setStep(2); }}
            />
          )}

          {step === 2 && job && order && (
            <PaymentScreen
              job={job}
              order={order}
              onSuccess={(data) => { setResult(data); setStep(3); }}
            />
          )}

          {step === 3 && result && (
            <SuccessScreen result={result} onReset={reset} />
          )}
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          Powered by Razorpay · Test Mode Active
        </p>
      </div>
    </div>
  );
}
